"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Chaque commande "make:" demande à Symfony de générer du code pour vous.
const COMMANDS = [
    {
        id: "entity",
        cmd: "make:entity Produit",
        labelKey: "Sf_console_cmd_entity",
        file: "src/Entity/Produit.php",
        created: ["Sf_console_file_entity1", "Sf_console_file_entity2"],
    },
    {
        id: "controller",
        cmd: "make:controller ProduitController",
        labelKey: "Sf_console_cmd_controller",
        file: "src/Controller/ProduitController.php",
        created: ["Sf_console_file_controller1", "Sf_console_file_controller2"],
    },
    {
        id: "crud",
        cmd: "make:crud Produit",
        labelKey: "Sf_console_cmd_crud",
        file: "templates/produit/index.html.twig",
        created: ["Sf_console_file_crud1", "Sf_console_file_crud2"],
    },
];

function CodePreview({ id }) {
    if (id === "entity") {
        return (
            <pre className="font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap break-words">
                <span className="text-fuchsia-400">class</span>
                <span className="text-zinc-400"> </span>
                <span className="text-sky-300">Produit</span>
                {"\n"}
                <span className="text-zinc-500">{"{"}</span>
                {"\n    "}
                <span className="text-fuchsia-400">private</span>
                <span className="text-sky-300"> int</span>
                <span className="text-sky-200"> $id</span>
                <span className="text-zinc-500">;</span>
                {"\n    "}
                <span className="text-fuchsia-400">private</span>
                <span className="text-sky-300"> string</span>
                <span className="text-sky-200"> $nom</span>
                <span className="text-zinc-500">;</span>
                {"\n    "}
                <span className="text-fuchsia-400">private</span>
                <span className="text-sky-300"> float</span>
                <span className="text-sky-200"> $prix</span>
                <span className="text-zinc-500">;</span>
                {"\n\n    "}
                <span className="text-zinc-500">
                    {"// getters et setters générés"}
                </span>
                {"\n"}
                <span className="text-zinc-500">{"}"}</span>
            </pre>
        );
    }
    if (id === "controller") {
        return (
            <pre className="font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap break-words">
                <span className="text-fuchsia-400">class</span>
                <span className="text-sky-300"> ProduitController</span>
                <span className="text-fuchsia-400"> extends</span>
                <span className="text-sky-300"> AbstractController</span>
                {"\n"}
                <span className="text-zinc-500">{"{"}</span>
                {"\n    "}
                <span className="text-amber-300">#[Route(</span>
                <span className="text-emerald-300">'/produits'</span>
                <span className="text-amber-300">)]</span>
                {"\n    "}
                <span className="text-fuchsia-400">public function</span>
                <span className="text-sky-400"> index</span>
                <span className="text-zinc-500">()</span>
                {"\n    "}
                <span className="text-zinc-500">{"{"}</span>
                {"\n        "}
                <span className="text-fuchsia-400">return</span>
                <span className="text-sky-200"> $this</span>
                <span className="text-zinc-400">-&gt;</span>
                <span className="text-sky-400">render</span>
                <span className="text-zinc-500">(</span>
                <span className="text-emerald-300">'index.html.twig'</span>
                <span className="text-zinc-500">);</span>
                {"\n    "}
                <span className="text-zinc-500">{"}"}</span>
                {"\n"}
                <span className="text-zinc-500">{"}"}</span>
            </pre>
        );
    }
    if (id === "crud") {
        return (
            <pre className="font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap break-words">
                <span className="text-zinc-400">&lt;table&gt;</span>
                {"\n"}
                <span className="text-amber-300">{"{% for produit in produits %}"}</span>
                {"\n  "}
                <span className="text-zinc-400">&lt;tr&gt;&lt;td&gt;</span>
                <span className="text-sky-300">{"{{ produit.nom }}"}</span>
                <span className="text-zinc-400">&lt;/td&gt;</span>
                {"\n      "}
                <span className="text-zinc-400">&lt;td&gt;</span>
                <span className="text-sky-300">{"{{ produit.prix }}"}</span>
                <span className="text-zinc-400"> €&lt;/td&gt;&lt;/tr&gt;</span>
                {"\n"}
                <span className="text-amber-300">{"{% endfor %}"}</span>
                {"\n"}
                <span className="text-zinc-400">&lt;/table&gt;</span>
            </pre>
        );
    }
    return null;
}

export default function SfConsole() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [running, setRunning] = useState(null);
    const [phase, setPhase] = useState("idle"); // typing | creating | idle
    const [doneIds, setDoneIds] = useState([]);
    const [activeId, setActiveId] = useState(null);

    function run(id) {
        if (running) return;
        if (doneIds.includes(id)) {
            setActiveId(id);
            return;
        }
        if (reduce) {
            setDoneIds((d) => [...d, id]);
            setActiveId(id);
            return;
        }
        setRunning(id);
        setPhase("typing");
    }

    useEffect(() => {
        if (!running) return undefined;
        if (phase === "typing") {
            const timer = setTimeout(() => setPhase("creating"), 600);
            return () => clearTimeout(timer);
        }
        if (phase === "creating") {
            const timer = setTimeout(() => {
                setDoneIds((d) => (d.includes(running) ? d : [...d, running]));
                setActiveId(running);
                setRunning(null);
                setPhase("idle");
            }, 850);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [running, phase]);

    const activeCmd = COMMANDS.find((c) => c.id === activeId);
    const filesCount = doneIds.reduce((n, id) => {
        const c = COMMANDS.find((x) => x.id === id);
        return n + (c ? c.created.length : 0);
    }, 0);
    const blocks = [...doneIds];
    if (running && !doneIds.includes(running)) blocks.push(running);

    return (
        <section id="symfony-console" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Sf_console_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Sf_console_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows monochromes */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-white/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-white/5 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="simple-icons:symfony"
                            className="w-3.5 h-3.5 text-white"
                            aria-hidden="true"
                        />
                        console
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Sf_console_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7 grid lg:grid-cols-2 gap-6 lg:gap-8">
                    {/* TERMINAL */}
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                            <SolarIcon
                                name="command-bold-duotone"
                                className="w-3.5 h-3.5"
                            />
                            {t("Sf_console_terminal_label")}
                        </div>

                        {/* Boutons de commande */}
                        <div className="flex flex-col gap-2 mb-4">
                            {COMMANDS.map((c) => {
                                const isDone = doneIds.includes(c.id);
                                const isRunning = running === c.id;
                                return (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => run(c.id)}
                                        disabled={!!running}
                                        className={`group flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left ring-1 transition active:scale-[0.99] disabled:cursor-not-allowed ${
                                            isDone
                                                ? "bg-emerald-500/[0.07] ring-emerald-400/30"
                                                : activeId === c.id
                                                  ? "bg-white/[0.1] ring-white/25"
                                                  : "bg-white/[0.05] ring-white/15 hover:bg-white/[0.09]"
                                        }`}
                                    >
                                        {isRunning ? (
                                            <SolarIcon
                                                name="refresh-bold-duotone"
                                                className="w-4 h-4 text-white shrink-0 animate-spin"
                                            />
                                        ) : isDone ? (
                                            <SolarIcon
                                                name="check-circle-bold"
                                                className="w-4 h-4 text-emerald-400 shrink-0"
                                            />
                                        ) : (
                                            <SolarIcon
                                                name="play-circle-bold"
                                                className="w-4 h-4 text-white shrink-0"
                                            />
                                        )}
                                        <span className="min-w-0">
                                            <span className="block text-[13px] font-semibold text-white leading-tight">
                                                {t(c.labelKey)}
                                            </span>
                                            <span className="block font-mono text-[11px] text-gray-400 truncate">
                                                php bin/console {c.cmd}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Sortie du terminal */}
                        <div className="rounded-xl bg-black/40 ring-1 ring-white/10 px-4 py-3 font-mono text-[12px] min-h-[120px]">
                            {blocks.length === 0 ? (
                                <span className="text-gray-500">
                                    {t("Sf_console_empty")}
                                </span>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {blocks.map((id) => {
                                        const c = COMMANDS.find((x) => x.id === id);
                                        const isRunning =
                                            running === id && !doneIds.includes(id);
                                        return (
                                            <div key={id}>
                                                <div className="text-gray-300">
                                                    <span className="text-emerald-400">
                                                        ${" "}
                                                    </span>
                                                    php bin/console {c.cmd}
                                                </div>
                                                {isRunning ? (
                                                    <div className="mt-1 inline-flex items-center gap-1.5 text-gray-400">
                                                        <SolarIcon
                                                            name="refresh-bold-duotone"
                                                            className="w-3.5 h-3.5 animate-spin"
                                                        />
                                                        {t("Sf_console_generating")}
                                                    </div>
                                                ) : (
                                                    <div className="mt-1 flex flex-col gap-0.5">
                                                        {c.created.map((fk) => (
                                                            <div
                                                                key={fk}
                                                                className="flex items-center gap-1.5 text-gray-300"
                                                            >
                                                                <SolarIcon
                                                                    name="check-circle-bold"
                                                                    className="w-3.5 h-3.5 text-emerald-400 shrink-0"
                                                                />
                                                                <span className="text-emerald-300/90">
                                                                    created:
                                                                </span>
                                                                <span className="truncate">
                                                                    {t(fk)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {filesCount > 0 && (
                            <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-gray-300">
                                <SolarIcon
                                    name="documents-bold-duotone"
                                    className="w-4 h-4 text-white"
                                />
                                {t("Sf_console_counter", { count: filesCount })}
                            </div>
                        )}
                    </div>

                    {/* APERÇU DU CODE GÉNÉRÉ */}
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                            <SolarIcon
                                name="code-square-bold"
                                className="w-3.5 h-3.5"
                            />
                            {t("Sf_console_code_label")}
                        </div>

                        <div className="rounded-2xl bg-black/30 ring-1 ring-white/10 overflow-hidden min-h-[260px]">
                            {activeCmd ? (
                                <>
                                    <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-white/[0.03]">
                                        <Icon
                                            icon="logos:php"
                                            className="w-4 h-4"
                                            aria-hidden="true"
                                        />
                                        <span className="font-mono text-[11.5px] text-gray-300 truncate">
                                            {activeCmd.file}
                                        </span>
                                    </div>
                                    <div className="p-4">
                                        <AnimatePresence mode="wait">
                                            <motion.div
                                                key={activeCmd.id}
                                                initial={
                                                    reduce
                                                        ? false
                                                        : { opacity: 0, y: 8 }
                                                }
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <CodePreview id={activeCmd.id} />
                                            </motion.div>
                                        </AnimatePresence>
                                    </div>
                                </>
                            ) : (
                                <div className="h-[260px] flex flex-col items-center justify-center gap-3 text-center px-6">
                                    <SolarIcon
                                        name="magic-stick-3-bold"
                                        className="w-8 h-8 text-white/70"
                                    />
                                    <span className="text-[13px] text-gray-400 font-geistSans">
                                        {t("Sf_console_placeholder")}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Sf_console_caption")}
            </p>
        </section>
    );
}
